/** Calendar — Google-style week view on the user's selected connected Google account + calendar. */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Plus, RefreshCw, CalendarDays } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Calendar as MiniMonth } from '../components/ui/calendar';
import { AccountPicker, connectGoogle } from '../components/calendar/AccountPicker';
import { WeekGrid } from '../components/calendar/WeekGrid';
import { CreateEventDialog, EventDialog } from '../components/calendar/EventDialogs';
import { startOfWeek, addDays } from '../components/calendar/calUtils';

function ConnectCard() {
  return (
    <div className="mx-auto max-w-md rounded-2xl border bg-card p-8 text-center" data-testid="calendar-connect-card">
      <CalendarDays className="mx-auto mb-3 h-10 w-10 text-[#1a73e8]" />
      <h2 className="text-lg font-semibold">Connect your Google account</h2>
      <p className="mt-1 text-sm text-muted-foreground">Google will ask which account to use. Your Gmail and Calendar stay connected to your login only.</p>
      <Button className="mt-5 bg-[#1a73e8] hover:bg-[#1765cc]" onClick={() => connectGoogle().catch((e) => toast.error(e.message))} data-testid="calendar-connect-btn">Connect Google account</Button>
    </div>
  );
}

export default function CalendarPage() {
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [week, setWeek] = useState(startOfWeek(new Date()));
  const [events, setEvents] = useState([]);
  const [state, setState] = useState('loading');
  const [creating, setCreating] = useState(null);
  const [open, setOpen] = useState(null);

  const loadInfo = useCallback(() => api.request('/calendar/accounts').then(setInfo).catch(() => setState('error')), []);
  const loadEvents = useCallback(async () => {
    setState('loading');
    try {
      const q = new URLSearchParams({ start: week.toISOString(), end: addDays(week, 7).toISOString(), max_results: '250' });
      const d = await api.request(`/calendar/events?${q}`);
      setEvents(d.events || []); setState('ok');
    } catch { setState('error'); }
  }, [week]);
  useEffect(() => { loadInfo(); if (params.get('success') === 'gmail_connected') toast.success('Google account connected'); }, [loadInfo, params]);
  useEffect(() => { if (info?.selected) loadEvents(); else if (info) setState('ok'); }, [info, loadEvents]);

  const sel = info?.selected;
  const title = `${week.toLocaleDateString([], { month: 'long' })}${addDays(week, 6).getMonth() !== week.getMonth() ? ` – ${addDays(week, 6).toLocaleDateString([], { month: 'long' })}` : ''} ${addDays(week, 6).getFullYear()}`;
  if (!info && state !== 'error') return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (info && !sel) return <div className="py-16"><ConnectCard /></div>;
  return (
    <div className="flex flex-col gap-4 lg:flex-row" data-testid="calendar-page">
      <aside className="w-full space-y-5 lg:w-64 lg:shrink-0">
        <Button onClick={() => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); setCreating(d); }} className="h-12 rounded-2xl bg-white px-5 text-foreground shadow-md hover:bg-[#f1f3f4] hover:shadow-lg dark:bg-card" data-testid="calendar-create-btn">
          <Plus className="mr-2 h-5 w-5 text-[#1a73e8]" /> Create</Button>
        <MiniMonth mode="single" selected={new Date()} onSelect={(d) => d && setWeek(startOfWeek(d))} className="rounded-xl border" />
        <AccountPicker info={info} onChanged={loadInfo} />
      </aside>
      <section className="min-w-0 flex-1 overflow-x-auto rounded-2xl border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setWeek(startOfWeek(new Date()))} data-testid="calendar-today-btn">Today</Button>
          <Button variant="ghost" size="icon" onClick={() => setWeek(addDays(week, -7))} data-testid="calendar-prev-btn"><ChevronLeft className="h-5 w-5" /></Button>
          <Button variant="ghost" size="icon" onClick={() => setWeek(addDays(week, 7))} data-testid="calendar-next-btn"><ChevronRight className="h-5 w-5" /></Button>
          <h1 className="text-xl font-normal" data-testid="calendar-title">{title}</h1>
          <span className="ml-auto truncate text-xs text-muted-foreground" data-testid="calendar-selected-account">{sel?.email} · {sel?.calendar_id === 'primary' ? 'Primary calendar' : sel?.calendar_id}</span>
          {state === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {state === 'error' ? (
          <div className="py-20 text-center" data-testid="calendar-error-state"><p className="text-sm text-muted-foreground">Couldn't load events from Google.</p>
            <Button variant="outline" className="mt-3" onClick={() => { loadInfo(); loadEvents(); }} data-testid="calendar-retry-btn"><RefreshCw className="mr-2 h-4 w-4" />Retry</Button></div>
        ) : <WeekGrid weekStart={week} events={events} onSlot={setCreating} onOpen={setOpen} />}
      </section>
      <CreateEventDialog at={creating} accountEmail={sel?.email} onClose={() => setCreating(null)} onCreated={() => { setCreating(null); loadEvents(); }} />
      <EventDialog ev={open} onClose={() => setOpen(null)} onDeleted={() => { setOpen(null); loadEvents(); }} />
    </div>
  );
}
